/**
 * PublicLandingView
 * Public-facing marketing page for busmgr.com
 * No authentication required.
 */

import React, { useState } from 'react';

var API_URL = process.env.REACT_APP_API_URL || '/api/v1';

// Annual pricing = monthly x 12 x 0.85 (15% off, rounded)
var PLANS = [
  {
    key: 'producer',
    name: 'Producer',
    monthlyPrice: 49,
    annualPrice: 500,
    tagline: 'Books, website, and online sales -- the foundation every business needs.',
    highlight: false,
    badge: null,
    description: "Full accounting, bank feeds, eCommerce, and your customer portal. The complete back-office foundation for producers who sell online and need their finances in order -- without modules they will never use.",
    includes: [
      'Double-entry accounting & general ledger',
      'Bank feeds via Plaid (automatic import)',
      'Bank reconciliation & journal entries',
      'Report builder & financial reports',
      'Receipt inbox',
      'eCommerce website & customer portal',
      'Events & booking support',
    ],
  },
  {
    key: 'trades',
    name: 'Trades',
    monthlyPrice: 69,
    annualPrice: 690,
    tagline: 'Books, website, and jobs for lawn care, HVAC, electrical, and plumbing.',
    highlight: false,
    badge: null,
    description: "The Farmer analog for service businesses. Ledger, Site Builder, standing routes, day map, and estimate-to-invoice — not farm Back Office. Payroll stays a $10/mo add-on.",
    includes: [
      'Double-entry accounting & general ledger',
      'Bank feeds, invoices, and Post to GL',
      'Site Builder',
      'Jobs, standing routes, and day map',
      'Estimate to job to invoice',
      'CPA portal for your accountant',
    ],
  },
  {
    key: 'grower',
    name: 'Grower',
    monthlyPrice: 69,
    annualPrice: 704,
    tagline: 'Your storefront and online orders -- on the same books as the farm stand.',
    highlight: false,
    badge: null,
    description: "Everything in Producer, plus an eCommerce storefront for online sales. Customers order from your site; you fulfill from the same catalog and ledger you already use.",
    includes: [
      'Everything in Producer',
      'eCommerce storefront',
      'Online order management',
      'Delivery zones',
      'Customer portal',
    ],
  },
  {
    key: 'rancher',
    name: 'Rancher',
    monthlyPrice: 79,
    annualPrice: 806,
    tagline: 'Raise it, sell it direct, account for every head -- one platform start to finish.',
    highlight: true,
    badge: 'Most Popular',
    description: "Back Office, retail POS, and Herds & Flocks in a single platform. Track your herd, run your market booth, and keep your books -- every sale ties back to your animals and your ledger.",
    includes: [
      'Everything in Producer',
      'Herds & Flocks livestock management',
      'Retail POS (market & farm stand)',
      'Stripe Terminal (M2 card reader)',
      'Animal records linked to product sales',
      'Inventory management & product catalog',
      'BusMgr Android POS app',
    ],
  },
  {
    key: 'market-director',
    name: 'Market Director',
    monthlyPrice: 79,
    annualPrice: 806,
    tagline: 'Run the whole market -- vendors, tokens, payouts, and a portal they manage themselves.',
    highlight: false,
    badge: null,
    description: "Built for farmers-market organizers. Manage your vendor roster, token economy, event payouts, and volunteers in Market Manager -- and give vendors a self-service portal to submit reports, see booth assignments, and check payout history. Full back-office accounting and an online presence behind it all.",
    includes: [
      'Everything in Producer',
      'Market Manager (vendors, tokens, payouts)',
      'Vendor Portal -- vendor self-service',
      'Roll Call & duty roster',
      'DUFB / SNAP token tracking',
      'eCommerce website & customer portal',
    ],
  },
  {
    key: 'market-vendor',
    name: 'Market Vendor',
    monthlyPrice: 89,
    annualPrice: 908,
    tagline: 'Built for the booth. Sell product, manage inventory, close your books the same day.',
    highlight: false,
    badge: null,
    description: "Everything in Producer, plus the full retail point-of-sale system. Accept card payments via Stripe Terminal, manage your product inventory, and watch every market sale flow automatically into your books.",
    includes: [
      'Everything in Producer',
      'Retail POS (market & farm stand)',
      'Stripe Terminal (M2 card reader)',
      'Inventory management & product catalog',
      'Sales automatically posted to GL',
      'BusMgr Android POS app',
      'Offline mode for spotty market Wi-Fi',
    ],
  },
  {
    key: 'food-trailer',
    name: 'Food Trailer',
    monthlyPrice: 99,
    annualPrice: 1010,
    tagline: 'Mobile-first ordering from first ticket to final close -- no paper, no friction.',
    highlight: false,
    badge: null,
    description: "Everything in Producer, plus the Restaurant POS and Kitchen Display System. Designed for mobile food operators -- table-free, ticket-based ordering built for the way a food trailer actually runs.",
    includes: [
      'Everything in Producer',
      'Mobile Restaurant POS',
      'Kitchen Display System (KDS)',
      'Menu management & modifiers',
      'Order routing to kitchen display',
      'Tab management & tip handling',
      'BusMgr Android POS app',
    ],
  },
  {
    key: 'farm-kitchen',
    name: 'Farm Kitchen',
    monthlyPrice: 119,
    annualPrice: 1214,
    tagline: 'From the pasture to the plate -- production records and service in one place.',
    highlight: false,
    badge: null,
    description: "Raise it, cook it, serve it -- and reconcile it all the same day. For butcher shops, farm-to-table operations, and value-added food producers who want their livestock records and food service running in the same system.",
    includes: [
      'Everything in Producer',
      'Herds & Flocks livestock management',
      'Mobile Restaurant POS',
      'Kitchen Display System (KDS)',
      'Processing records tied to menu items',
      'Menu management & modifiers',
      'BusMgr Android POS app',
    ],
  },
  {
    key: 'market-and-trailer',
    name: 'Market & Trailer',
    monthlyPrice: 129,
    annualPrice: 1316,
    tagline: 'Run your booth and your trailer side by side. Two operations, one set of books.',
    highlight: false,
    badge: null,
    description: "For operators who run both a retail booth and a food trailer at the same events. Two complete point-of-sale systems, one shared product catalog, one chart of accounts. Built for busy market days where every minute counts.",
    includes: [
      'Everything in Producer',
      'Retail POS (market & farm stand)',
      'Mobile Restaurant POS',
      'Kitchen Display System (KDS)',
      'Stripe Terminal (M2 card reader)',
      'Shared product catalog & inventory',
      'BusMgr Android POS app',
    ],
  },
  {
    key: 'i-want-it-all',
    name: 'I Want It All',
    monthlyPrice: 149,
    annualPrice: 1520,
    tagline: 'Every module. Every capability. The complete platform for the full operation.',
    highlight: false,
    badge: 'Best Value',
    description: "The full stack -- livestock management, retail sales, food service, accounting, eCommerce, and your customer portal. Everything a modern agricultural operation needs in a single platform, managed from a single login.",
    includes: [
      'Everything in Producer',
      'Herds & Flocks livestock management',
      'Retail POS (market & farm stand)',
      'Mobile Restaurant POS & KDS',
      'Stripe Terminal (M2 card reader)',
      'eCommerce website & customer portal',
      'BusMgr Android POS app',
    ],
  },
];

var FEATURES = [
  {
    icon: '📊',
    title: 'Double-Entry Accounting',
    desc: 'Full general ledger, journal entries, bank reconciliation, and financial reports. Built for small-scale agricultural and independent business operators who need real accounting -- not a simplified checkbook.',
  },
  {
    icon: '🛒',
    title: 'eCommerce & Customer Portal',
    desc: 'A branded online store with your products, custom pages, event bookings, and order management. Ready to go on your own domain with no separate subscription required.',
  },
  {
    icon: '💳',
    title: 'Market POS',
    desc: 'Stripe Terminal hardware and an Android POS app built for farm stands and market vendors. Full retail inventory management with every sale posted directly to your ledger.',
  },
  {
    icon: '🍔',
    title: 'Food Trailer POS & KDS',
    desc: 'Mobile-first ticket ordering and a kitchen display system for food trailers and farm kitchens. No paper tickets. No per-device subscriptions.',
  },
  {
    icon: '🏪',
    title: 'Market Manager & Vendor Portal',
    desc: 'Run a farmers market end to end: vendor roster, booth assignments, roll call, stall fees, token economy, DUFB/SNAP tracking, and event payouts. Vendors get their own self-service portal to submit reports and check payout history.',
  },
  {
    icon: '🐄',
    title: 'Herds & Flocks',
    desc: 'Track animals, pastures, breeding, processing records, and rainfall alongside your financials. Built for cattle, hog, goat, sheep, and poultry operations with every record connected to your books.',
  },
  {
    icon: '🏦',
    title: 'Bank Feeds',
    desc: 'Connect your bank accounts for automatic transaction import and one-click reconciliation. No manual CSV exports. Your transactions, your way.',
  },
];

// Business model to tier recommendation matrix
var BUSINESS_MODELS = [
  { model: 'CSA / Subscription Farm', description: 'Online orders, customer accounts, accounting', tier: 'Producer', key: 'producer' },
  { model: 'Lawn Care / Landscaping', description: 'Standing routes, day map, jobs to invoice, books', tier: 'Trades', key: 'trades' },
  { model: 'HVAC / Electrical / Plumbing', description: 'Service jobs, estimates, schedule, accounting', tier: 'Trades', key: 'trades' },
  { model: 'Market Garden / Produce', description: 'Farmers market sales, inventory, bookkeeping', tier: 'Market Vendor', key: 'market-vendor' },
  { model: 'Farmers Market Organizer', description: 'Vendor roster, tokens, payouts, vendor portal', tier: 'Market Director', key: 'market-director' },
  { model: 'Market Association / Nonprofit', description: 'Member vendors, DUFB/SNAP, event reconciliation', tier: 'Market Director', key: 'market-director' },
  { model: 'Multi-Vendor Market', description: 'Booth assignments, roll call, stall fees, payouts', tier: 'Market Director', key: 'market-director' },
  { model: 'Cow-Calf / Livestock', description: 'Herd records, pastures, breeding, financials', tier: 'Rancher', key: 'rancher' },
  { model: 'Direct-Market Ranch', description: 'Livestock management plus market booth sales', tier: 'Rancher', key: 'rancher' },
  { model: 'Food Trailer / Mobile Kitchen', description: 'Ticket-based ordering, KDS, accounting', tier: 'Food Trailer', key: 'food-trailer' },
  { model: 'Butcher Shop / Farm Kitchen', description: 'Livestock, value-added products, food service', tier: 'Farm Kitchen', key: 'farm-kitchen' },
  { model: 'Market Vendor & Food Trailer', description: 'Retail booth plus food trailer at same events', tier: 'Market & Trailer', key: 'market-and-trailer' },
  { model: 'U-Pick / Agritourism', description: 'Event bookings, online ticketing, website, accounting', tier: 'Producer', key: 'producer' },
  { model: 'Farm Tours & Workshops', description: 'Event scheduling, customer portal, financials', tier: 'Producer', key: 'producer' },
  { model: 'Farm Stay / Lodging', description: 'Booking management, eCommerce, accounting', tier: 'Producer', key: 'producer' },
  { model: 'Fiber / Specialty Craft', description: 'Online store, customer accounts, bookkeeping', tier: 'Producer', key: 'producer' },
  { model: 'Full Diversified Operation', description: 'All of the above', tier: 'I Want It All', key: 'i-want-it-all' },
];

var styles = {
  root: {
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    color: '#1a1a1a',
    background: '#fff',
    minHeight: '100vh',
  },
  header: {
    background: '#1a3a2a',
    color: '#fff',
    padding: '0 2rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 64,
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
  },
  logo: { fontWeight: 700, fontSize: '1.25rem', letterSpacing: '-0.02em', color: '#7ecba1' },
  headerNav: { display: 'flex', gap: '1.25rem', alignItems: 'center' },
  headerLink: {
    color: '#cde8d8', textDecoration: 'none', fontSize: '0.9rem',
    cursor: 'pointer', background: 'none', border: 'none', padding: 0,
  },
  headerCta: {
    background: '#7ecba1', color: '#1a3a2a', border: 'none',
    borderRadius: 6, padding: '0.45rem 1.1rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer',
  },
  hero: {
    background: 'linear-gradient(135deg, #1a3a2a 0%, #2d5a3d 60%, #3a7a50 100%)',
    color: '#fff', padding: '5rem 2rem 4rem', textAlign: 'center',
  },
  heroEyebrow: {
    fontSize: '0.78rem', fontWeight: 700, letterSpacing: '0.12em',
    textTransform: 'uppercase', color: '#7ecba1', marginBottom: '1rem',
  },
  heroTitle: {
    fontSize: 'clamp(2rem, 5vw, 3.2rem)', fontWeight: 800,
    margin: '0 auto 1.25rem', maxWidth: 720, lineHeight: 1.15,
  },
  heroSub: {
    fontSize: '1.1rem', color: '#b5dfc6', maxWidth: 640,
    margin: '0 auto 1.5rem', lineHeight: 1.65,
  },
  heroCallout: {
    display: 'inline-block',
    background: 'rgba(126,203,161,0.15)',
    border: '1px solid rgba(126,203,161,0.4)',
    borderRadius: 8, padding: '0.6rem 1.5rem',
    color: '#7ecba1', fontSize: '0.95rem', marginBottom: '2.5rem',
  },
  heroCtas: { display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' },
  btnPrimary: {
    background: '#7ecba1', color: '#1a3a2a', border: 'none',
    borderRadius: 8, padding: '0.8rem 2rem', fontWeight: 700, fontSize: '1rem', cursor: 'pointer',
  },
  btnOutline: {
    background: 'transparent', color: '#fff',
    border: '2px solid rgba(255,255,255,0.4)',
    borderRadius: 8, padding: '0.8rem 2rem', fontWeight: 600, fontSize: '1rem', cursor: 'pointer',
  },
  section: { padding: '4rem 2rem', maxWidth: 1100, margin: '0 auto' },
  sectionTitle: { fontSize: '1.8rem', fontWeight: 700, textAlign: 'center', marginBottom: '0.5rem' },
  sectionSub: { textAlign: 'center', color: '#555', marginBottom: '3rem', fontSize: '1.0rem', lineHeight: 1.6 },
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' },
  featureCard: { background: '#f8faf9', border: '1px solid #e4ede8', borderRadius: 12, padding: '1.5rem' },
  featureIcon: { fontSize: '1.8rem', marginBottom: '0.75rem' },
  featureTitle: { fontWeight: 700, marginBottom: '0.4rem', fontSize: '1rem' },
  featureDesc: { color: '#555', fontSize: '0.88rem', lineHeight: 1.6, margin: 0 },
  // Business model matrix
  matrixBg: { background: '#1a3a2a', padding: '4rem 2rem' },
  matrixTitle: { fontSize: '1.8rem', fontWeight: 700, textAlign: 'center', marginBottom: '0.5rem', color: '#fff' },
  matrixSub: { textAlign: 'center', color: '#a8d8bc', marginBottom: '2.5rem', fontSize: '1.0rem', lineHeight: 1.6 },
  matrixGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '0.85rem',
    maxWidth: 1100,
    margin: '0 auto',
  },
  matrixCard: {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid rgba(126,203,161,0.2)',
    borderRadius: 10,
    padding: '1rem 1.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  matrixModel: { fontWeight: 700, fontSize: '0.9rem', color: '#fff', marginBottom: '0.2rem' },
  matrixDesc: { fontSize: '0.78rem', color: '#7ecba1', lineHeight: 1.4 },
  matrixTierBadge: {
    background: 'rgba(126,203,161,0.15)',
    border: '1px solid rgba(126,203,161,0.35)',
    color: '#7ecba1',
    borderRadius: 20,
    padding: '0.2rem 0.7rem',
    fontSize: '0.72rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  // Pricing
  pricingBg: { background: '#f3f8f5', padding: '4rem 2rem' },
  billingToggle: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '0.75rem', marginBottom: '2.5rem', flexWrap: 'wrap',
  },
  toggleLabel: { fontSize: '0.95rem', color: '#444', fontWeight: 500 },
  savingsBadge: {
    background: '#7ecba1', color: '#1a3a2a',
    borderRadius: 10, padding: '0.1rem 0.5rem', fontSize: '0.7rem', fontWeight: 800,
  },
  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(255px, 1fr))',
    gap: '1.25rem', maxWidth: 1200, margin: '0 auto',
  },
  planName: { fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.2rem' },
  planPrice: { display: 'flex', alignItems: 'baseline', gap: '0.2rem', marginBottom: '0.15rem' },
  planAmount: { fontSize: '2.2rem', fontWeight: 800, lineHeight: 1 },
  planFeatures: { listStyle: 'none', padding: 0, margin: '0 0 1.25rem', flex: 1 },
  valueBox: {
    background: '#fff', border: '1px solid #dde8e3', borderRadius: 14,
    padding: '2rem 2.5rem', maxWidth: 900, margin: '3rem auto 0', textAlign: 'center',
    boxShadow: '0 2px 16px rgba(26,58,42,0.06)',
  },
  valueTitle: { fontSize: '1.15rem', fontWeight: 700, marginBottom: '0.5rem', color: '#1a3a2a' },
  valueText: { fontSize: '0.95rem', color: '#444', lineHeight: 1.7, margin: 0 },
  // Form
  formSection: { padding: '4rem 2rem', maxWidth: 640, margin: '0 auto' },
  formCard: {
    background: '#fff', border: '1px solid #dde8e3', borderRadius: 16,
    padding: '2.5rem', boxShadow: '0 4px 24px rgba(26,58,42,0.08)',
  },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' },
  formGroup: { marginBottom: '1rem' },
  label: { display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.35rem', color: '#333' },
  input: {
    width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #cdd8d3',
    borderRadius: 7, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box',
  },
  select: {
    width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #cdd8d3',
    borderRadius: 7, fontSize: '0.95rem', background: '#fff', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '0.6rem 0.8rem', border: '1px solid #cdd8d3',
    borderRadius: 7, fontSize: '0.95rem', resize: 'vertical', minHeight: 80, boxSizing: 'border-box',
  },
  submitBtn: {
    width: '100%', background: '#1a3a2a', color: '#fff', border: 'none',
    borderRadius: 8, padding: '0.85rem', fontWeight: 700, fontSize: '1rem', cursor: 'pointer', marginTop: '0.5rem',
  },
  submitBtnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  errorBanner: {
    background: '#fef2f2', border: '1px solid #fca5a5', color: '#b91c1c',
    borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.9rem',
  },
  successBox: {
    background: '#f0faf4', border: '1px solid #7ecba1',
    borderRadius: 12, padding: '2.5rem', textAlign: 'center',
  },
  successTitle: { fontWeight: 700, fontSize: '1.3rem', color: '#1a3a2a', marginBottom: '0.5rem' },
  successMsg: { color: '#444', fontSize: '0.95rem' },
  footer: { background: '#1a3a2a', color: '#8aa89a', textAlign: 'center', padding: '2rem', fontSize: '0.85rem' },
  footerLink: { color: '#e8f5ee', textDecoration: 'underline', fontWeight: 600 },
};

function togglePillStyle(active) {
  return {
    display: 'inline-flex', alignItems: 'center',
    background: active ? '#1a3a2a' : '#e0ede7',
    color: active ? '#fff' : '#555',
    border: 'none', borderRadius: 20, padding: '0.35rem 1rem',
    fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', gap: '0.4rem',
  };
}
function planCardStyle(highlight) {
  return {
    background: highlight ? '#1a3a2a' : '#fff',
    color: highlight ? '#fff' : '#1a1a1a',
    border: highlight ? '2px solid #7ecba1' : '1px solid #dde8e3',
    borderRadius: 14, padding: '1.75rem',
    position: 'relative', display: 'flex', flexDirection: 'column',
  };
}
function planBadgeStyle(highlight) {
  return {
    position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
    background: highlight ? '#7ecba1' : '#f59e0b',
    color: highlight ? '#1a3a2a' : '#fff',
    fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.08em',
    textTransform: 'uppercase', padding: '0.2rem 0.75rem', borderRadius: 20, whiteSpace: 'nowrap',
  };
}
function planTaglineStyle(highlight) {
  return { fontSize: '0.8rem', color: highlight ? '#a8d8bc' : '#666', marginBottom: '0.85rem', lineHeight: 1.4 };
}
function planDescStyle(highlight) {
  return { fontSize: '0.78rem', color: highlight ? '#b5dfc6' : '#555', marginBottom: '1rem', lineHeight: 1.55 };
}
function planPeriodStyle(highlight) {
  return { fontSize: '0.85rem', color: highlight ? '#a8d8bc' : '#777' };
}
function planAnnualStyle(highlight) {
  return { fontSize: '0.73rem', color: highlight ? '#7ecba1' : '#888', marginBottom: '0.85rem' };
}
function planFeatureStyle(highlight) {
  return {
    display: 'flex', gap: '0.45rem', alignItems: 'flex-start',
    fontSize: '0.8rem', color: highlight ? '#cde8d8' : '#333', marginBottom: '0.4rem',
  };
}
function checkmarkStyle(highlight) {
  return { color: highlight ? '#7ecba1' : '#2d7a4f', fontWeight: 700, flexShrink: 0, marginTop: 1, fontSize: '0.82rem' };
}
function planBtnStyle(highlight) {
  return {
    background: highlight ? '#7ecba1' : '#1a3a2a',
    color: highlight ? '#1a3a2a' : '#fff',
    border: 'none', borderRadius: 8, padding: '0.7rem',
    fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', width: '100%', textAlign: 'center',
  };
}

export default function PublicLandingView() {
  var [annual, setAnnual] = useState(false);
  var [form, setForm] = useState({
    firstName: '', lastName: '', businessName: '',
    email: '', phone: '', planInterest: '', notes: '',
  });
  var [submitting, setSubmitting] = useState(false);
  var [submitted, setSubmitted] = useState(false);
  var [error, setError] = useState(null);
  var formRef = React.useRef(null);

  function scrollToForm(planKey) {
    if (planKey) {
      setForm(function(prev) { return Object.assign({}, prev, { planInterest: planKey }); });
    }
    if (formRef.current) { formRef.current.scrollIntoView({ behavior: 'smooth' }); }
  }

  function handleChange(e) {
    var name = e.target.name;
    var value = e.target.value;
    setForm(function(prev) { return Object.assign({}, prev, { [name]: value }); });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      var res = await fetch(API_URL + '/site-public/signup-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: form.firstName, lastName: form.lastName,
          businessName: form.businessName, email: form.email,
          phone: form.phone, planInterest: form.planInterest || 'unsure', notes: form.notes,
          source: 'busmgr.com',
        }),
      });
      var raw = await res.text();
      var data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch (parseErr) {
        throw new Error('Unable to reach the API. Confirm the backend is running on port 3000.');
      }
      if (!res.ok) {
        var details = (data && data.details) || (data && data.errors) || [];
        var firstMsg = details[0] && (details[0].msg || details[0].message);
        throw new Error(firstMsg || (data && data.message) || 'Something went wrong.');
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.root}>

      {/* Header */}
      <header style={styles.header}>
        <span style={styles.logo}>Business Manager</span>
        <nav style={styles.headerNav}>
          <button style={styles.headerLink} onClick={function() { var el = document.getElementById('features'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>Features</button>
          <button style={styles.headerLink} onClick={function() { var el = document.getElementById('who'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>Who It's For</button>
          <button style={styles.headerLink} onClick={function() { var el = document.getElementById('pricing'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>Pricing</button>
          <button style={styles.headerCta} onClick={function() { scrollToForm(); }}>Request Access</button>
          <a href="/admin" style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.72rem', textDecoration: 'none', marginLeft: '0.25rem' }}>Login</a>
        </nav>
      </header>

      {/* Hero */}
      <section style={styles.hero}>
        <p style={styles.heroEyebrow}>Built by a regenerative farmer for small-scale agriculture and independent producers</p>
        <h1 style={styles.heroTitle}>One platform. Your tools. Your terms.</h1>
        <p style={styles.heroSub}>
          Business Manager consolidates everything you need to run your operation into a single integrated platform.
          Choose the modules that fit your business. Nothing you do not need. Everything you do.
        </p>
        <div style={styles.heroCallout}>Starting at $49/mo -- pick only what your operation uses</div>
        <div style={styles.heroCtas}>
          <button style={styles.btnPrimary} onClick={function() { scrollToForm(); }}>Request Access</button>
          <button style={styles.btnOutline} onClick={function() { var el = document.getElementById('features'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}>See Features</button>
        </div>
      </section>

      {/* Features */}
      <section id="features" style={styles.section}>
        <h2 style={styles.sectionTitle}>Everything in one place, the way it should be</h2>
        <p style={styles.sectionSub}>
          Built by someone who has worked the land and the books and the sales and the setup. Every module shares one database, one login,
          and one chart of accounts -- so your livestock records, your sales, and your financials are always connected.
        </p>
        <div style={styles.featuresGrid}>
          {FEATURES.map(function(f) {
            return (
              <div key={f.title} style={styles.featureCard}>
                <div style={styles.featureIcon}>{f.icon}</div>
                <div style={styles.featureTitle}>{f.title}</div>
                <p style={styles.featureDesc}>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Who It's For -- Business Model Matrix */}
      <div id="who" style={styles.matrixBg}>
        <div style={{ maxWidth: 1100, margin: '0 auto' }}>
          <h2 style={styles.matrixTitle}>Built for your kind of operation</h2>
          <p style={styles.matrixSub}>
            From u-pick orchards and farm tours to cattle ranches, food trailers, and diversified market operations --
            Business Manager is designed around the way agricultural businesses actually work.
            Find your operation below and see which tier fits.
          </p>
          <div style={styles.matrixGrid}>
            {BUSINESS_MODELS.map(function(bm) {
              return (
                <div
                  key={bm.model}
                  style={styles.matrixCard}
                  onClick={function() { scrollToForm(bm.key); }}
                  title={'Request access to the ' + bm.tier + ' tier'}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={styles.matrixModel}>{bm.model}</div>
                    <div style={styles.matrixDesc}>{bm.description}</div>
                  </div>
                  <div style={styles.matrixTierBadge}>{bm.tier}</div>
                </div>
              );
            })}
          </div>
          <p style={{ textAlign: 'center', color: 'rgba(126,203,161,0.6)', fontSize: '0.8rem', marginTop: '1.5rem' }}>
            Click any operation type to jump to the request form with your recommended tier pre-selected.
          </p>
        </div>
      </div>

      {/* Pricing */}
      <div id="pricing" style={styles.pricingBg}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <h2 style={Object.assign({}, styles.sectionTitle, { paddingTop: 0 })}>Pick the modules you need</h2>
          <p style={styles.sectionSub}>
            Every tier includes full accounting, bank feeds, eCommerce, and your customer portal.
            Add the modules your operation actually uses.
          </p>

          {/* Billing toggle */}
          <div style={styles.billingToggle}>
            <span style={styles.toggleLabel}>Billing:</span>
            <button style={togglePillStyle(!annual)} onClick={function() { setAnnual(false); }}>Monthly</button>
            <button style={togglePillStyle(annual)} onClick={function() { setAnnual(true); }}>
              Annual &nbsp;<span style={styles.savingsBadge}>Save 15%</span>
            </button>
          </div>

          {/* Plan cards */}
          <div style={styles.pricingGrid}>
            {PLANS.map(function(plan) {
              var displayPrice = annual ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice;
              var annualNote = annual
                ? ('$' + plan.annualPrice + '/yr -- billed annually')
                : ('$' + plan.annualPrice + '/yr if billed annually');
              return (
                <div key={plan.key} style={planCardStyle(plan.highlight)}>
                  {plan.badge && <div style={planBadgeStyle(plan.highlight)}>{plan.badge}</div>}
                  <div style={styles.planName}>{plan.name}</div>
                  <div style={planTaglineStyle(plan.highlight)}>{plan.tagline}</div>
                  <div style={styles.planPrice}>
                    <span style={styles.planAmount}>${displayPrice}</span>
                    <span style={planPeriodStyle(plan.highlight)}>/mo</span>
                  </div>
                  <div style={planAnnualStyle(plan.highlight)}>{annualNote}</div>
                  <div style={planDescStyle(plan.highlight)}>{plan.description}</div>
                  <ul style={styles.planFeatures}>
                    {plan.includes.map(function(f) {
                      return (
                        <li key={f} style={planFeatureStyle(plan.highlight)}>
                          <span style={checkmarkStyle(plan.highlight)}>&#10003;</span>
                          <span>{f}</span>
                        </li>
                      );
                    })}
                  </ul>
                  <button style={planBtnStyle(plan.highlight)} onClick={function() { scrollToForm(plan.key); }}>
                    Get Started with {plan.name}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Value statement */}
          <div style={styles.valueBox}>
            <div style={styles.valueTitle}>One platform. One source of truth.</div>
            <p style={styles.valueText}>
              Most small operators are running their business across multiple disconnected tools -- and spending more time
              moving data between them than running their operation. Business Manager puts your accounting, sales,
              point of sale, livestock records, event bookings, and customer-facing website into a single system.
              One login. Built by someone who has worked the land and knows what it actually takes to run it.
            </p>
          </div>
        </div>
      </div>

      {/* Request form */}
      <section ref={formRef} id="request" style={styles.formSection}>
        <h2 style={Object.assign({}, styles.sectionTitle, { textAlign: 'left' })}>Request Access</h2>
        <p style={Object.assign({}, styles.sectionSub, { textAlign: 'left', marginBottom: '2rem' })}>
          Tell us about your operation and we will be in touch within one business day.
        </p>
        <div style={styles.formCard}>
          {submitted ? (
            <div style={styles.successBox}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>&#9989;</div>
              <div style={styles.successTitle}>Request received!</div>
              <p style={styles.successMsg}>Thank you for your interest in Business Manager. We will be in touch within one business day.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {error && <div style={styles.errorBanner}>{error}</div>}
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="firstName">First Name *</label>
                  <input id="firstName" name="firstName" style={styles.input} value={form.firstName} onChange={handleChange} required autoComplete="given-name" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="lastName">Last Name *</label>
                  <input id="lastName" name="lastName" style={styles.input} value={form.lastName} onChange={handleChange} required autoComplete="family-name" />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="businessName">Business Name *</label>
                <input id="businessName" name="businessName" style={styles.input} value={form.businessName} onChange={handleChange} required autoComplete="organization" />
              </div>
              <div style={styles.formRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="email">Email Address *</label>
                  <input id="email" name="email" type="email" style={styles.input} value={form.email} onChange={handleChange} required autoComplete="email" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label} htmlFor="phone">Phone (optional)</label>
                  <input id="phone" name="phone" type="tel" style={styles.input} value={form.phone} onChange={handleChange} autoComplete="tel" />
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="planInterest">Plan Interest</label>
                <select id="planInterest" name="planInterest" style={styles.select} value={form.planInterest} onChange={handleChange}>
                  <option value="">-- Not sure yet --</option>
                  <option value="producer">Producer -- $49/mo</option>
                  <option value="trades">Trades -- $69/mo</option>
                  <option value="grower">Grower -- $69/mo</option>
                  <option value="rancher">Rancher -- $79/mo</option>
                  <option value="market-director">Market Director -- $79/mo</option>
                  <option value="market-vendor">Market Vendor -- $89/mo</option>
                  <option value="food-trailer">Food Trailer -- $99/mo</option>
                  <option value="farm-kitchen">Farm Kitchen -- $119/mo</option>
                  <option value="market-and-trailer">Market &amp; Trailer -- $129/mo</option>
                  <option value="i-want-it-all">I Want It All -- $149/mo</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="notes">Tell us about your operation</label>
                <textarea id="notes" name="notes" style={styles.textarea} value={form.notes} onChange={handleChange} placeholder="What do you raise or produce? What tools are you using today? Any questions?" />
              </div>
              <button type="submit" disabled={submitting} style={Object.assign({}, styles.submitBtn, submitting ? styles.submitBtnDisabled : {})}>
                {submitting ? 'Sending...' : 'Submit Request'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={{ margin: 0 }}>
          &copy; {new Date().getFullYear()} CR Hood Solutions, LLC &middot; Bullard, TX &middot;{' '}
          <a href="/legal/privacy" style={styles.footerLink}>Privacy Policy</a>
          {' · '}
          <a href="/legal/terms" style={styles.footerLink}>Terms</a>
          {' · '}
          <a href="/legal/sms-opt-in" style={styles.footerLink}>SMS opt-in</a>
        </p>
      </footer>

    </div>
  );
}
